// src/modules/ml/FeatureEng.jsx
// Feature Engineering — standalone ML sidebar module
// Upload data → explore → encode → transform → PCA → export engineered features

import React, { useState, useRef } from 'react';
import {
  BarChart, Bar, ScatterChart, Scatter, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, ReferenceLine,
} from 'recharts';
import {
  parseCSVForML, inferTypes, featureStats, oneHotEncode, labelEncode,
  binColumn, polyFeatures, interactionFeature, logTransform, normalizeDataset,
  mutualInfo, runPCA, pearson, parseNum, mean, std, generateMockDataset,
} from '../../utils/mlProcessing.js';
import { Panel, Metric, Tag, Spinner, InfoBox, LogPanel, timestamp } from '../../components/ui.jsx';

const P = ['var(--cyan)','var(--green)','var(--violet)','var(--gold)','var(--red)','var(--orange)','#f472b6','#34d399'];

const Sel = ({ value, onChange, options, label, style }) => (
  <div style={style}>
    {label && <div style={{fontSize:'9px',color:'var(--t3)',letterSpacing:'.1em',marginBottom:'4px'}}>{label}</div>}
    <select value={value} onChange={e=>onChange(e.target.value)} style={{width:'100%',background:'var(--deep)',border:'1px solid var(--b1)',color:'var(--t1)',padding:'7px 8px',fontSize:'11px',fontFamily:'JetBrains Mono',borderRadius:'2px',outline:'none'}}>
      {options.map(o=><option key={o.value??o} value={o.value??o}>{o.label??o}</option>)}
    </select>
  </div>
);
const Btn = ({ onClick, children, color='var(--cyan)', disabled, style }) => (
  <button onClick={onClick} disabled={disabled} style={{padding:'9px 18px',background:color+'18',border:`1px solid ${color}`,color,fontSize:'10px',cursor:disabled?'not-allowed':'pointer',borderRadius:'3px',fontFamily:'JetBrains Mono',fontWeight:700,letterSpacing:'.08em',opacity:disabled?.4:1,transition:'all .15s',...style}}>{children}</button>
);

const TABS = ['upload','overview','distributions','correlations','encode','transform','scaling','pca','export'];

export default function FeatureEng() {
  const [tab, setTab] = useState('upload');
  const [orig, setOrig] = useState(null);   // original dataset
  const [data, setData] = useState(null);   // working (transformed) dataset
  const [logs, setLogs] = useState([{ts:'00:00:00',msg:'Feature Engineering Lab — upload or pick a sample dataset.',t:'info'}]);
  const [selCol, setSelCol] = useState('');
  const [selCols, setSelCols] = useState([]);
  const [encodeCol, setEncodeCol] = useState('');
  const [encodeMethod, setEncodeMethod] = useState('onehot');
  const [binCol, setBinCol] = useState('');
  const [binN, setBinN] = useState(5);
  const [binStrat, setBinStrat] = useState('equal_width');
  const [normMethod, setNormMethod] = useState('minmax');
  const [logCol, setLogCol] = useState('');
  const [polyC1, setPolyC1] = useState('');
  const [polyC2, setPolyC2] = useState('');
  const [intC1, setIntC1] = useState('');
  const [intC2, setIntC2] = useState('');
  const [pcaResult, setPcaResult] = useState(null);
  const [pcaCols, setPcaCols] = useState([]);
  const [miMatrix, setMiMatrix] = useState([]);
  const [scaleCols, setScaleCols] = useState([]);
  const [scaleMethod, setScaleMethod] = useState('minmax');
  const [scaleResult, setScaleResult] = useState(null);
  const [corrMatrix, setCorrMatrix] = useState([]);
  const fileRef = useRef();

  const log = (msg,t='info') => setLogs(l=>[...l.slice(-80),{ts:timestamp(),msg,t}]);

  const loadData = (text, name, direct) => {
    const parsed = direct || (text ? parseCSVForML(text) : null);
    if (!parsed) { log('Parse error','err'); return; }
    const types = inferTypes(parsed.headers, parsed.rows);
    const nc = types.filter(t=>t.type==='numeric').map(t=>t.col);
    const cc = types.filter(t=>t.type==='categorical').map(t=>t.col);
    const d = {...parsed, types};
    setOrig(d); setData(d);
    setSelCol(nc[0]||parsed.headers[0]);
    setSelCols(nc.slice(0,4));
    setPcaCols(nc.slice(0,4));
    setEncodeCol(cc[0]||'');
    setBinCol(nc[0]||''); setLogCol(nc[0]||'');
    setPolyC1(nc[0]||''); setPolyC2(nc[1]||nc[0]||'');
    setIntC1(nc[0]||''); setIntC2(nc[1]||nc[0]||'');
    log(`✓ "${name}": ${parsed.rows.length} rows × ${parsed.headers.length} cols | ${nc.length} numeric · ${cc.length} categorical`,'ok');
    setTab('overview');
  };

  const rows = data?.rows||[], headers = data?.headers||[], types = data?.types||[];
  const nc = types.filter(t=>t.type==='numeric').map(t=>t.col);
  const cc = types.filter(t=>t.type==='categorical').map(t=>t.col);

  const applyTransform = (result, description) => {
    const newH = result.headers || [...new Set([...headers, ...(result.newCols||[]), result.newCol].filter(Boolean))];
    const newTypes = inferTypes(newH, result.rows);
    setData({rows:result.rows, headers:newH, types:newTypes});
    log(description, 'ok');
  };

  return (
    <div className="anim-fadeup" style={{display:'flex',flexDirection:'column',gap:'12px'}}>
      {/* Tabs */}
      <div style={{display:'flex',gap:'3px',background:'var(--deep)',padding:'4px',borderRadius:'3px',border:'1px solid var(--b0)',overflowX:'auto'}}>
        {TABS.map(t=>(
          <button key={t} onClick={()=>setTab(t)} style={{padding:'6px 14px',border:`1px solid ${tab===t?'var(--cyan)':'transparent'}`,background:tab===t?'var(--gcyan)':'transparent',color:tab===t?'var(--cyan)':'var(--t3)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono',letterSpacing:'.07em',whiteSpace:'nowrap',fontWeight:tab===t?700:400}}>
            {t.toUpperCase()}
          </button>
        ))}
      </div>

      {/* ── UPLOAD ── */}
      {tab==='upload' && (
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'14px'}}>
          <div onDragOver={e=>{e.preventDefault()}} onDrop={e=>{e.preventDefault();const f=e.dataTransfer.files[0];if(f){const r=new FileReader();r.onload=ev=>loadData(ev.target.result,f.name);r.readAsText(f);}}}
            onClick={()=>fileRef.current?.click()}
            style={{border:'2px dashed var(--b1)',borderRadius:'4px',padding:'52px 24px',textAlign:'center',cursor:'pointer',background:'var(--surface)'}}>
            <input ref={fileRef} type="file" accept=".csv" style={{display:'none'}} onChange={e=>{const f=e.target.files[0];if(f){const r=new FileReader();r.onload=ev=>loadData(ev.target.result,f.name);r.readAsText(f);}}}/>
            <div style={{fontSize:'40px',opacity:.3,marginBottom:'14px'}}>⬟</div>
            <div style={{fontFamily:'Syne',fontWeight:700,fontSize:'18px',marginBottom:'7px'}}>Drop CSV File</div>
            <div style={{fontSize:'12px',color:'var(--t3)'}}>or click to browse</div>
          </div>
          <Panel title="SAMPLE DATASETS" accent="var(--cyan)">
            {[['Linear (x1,x2→y)','linear','2 features, numeric target'],['Housing Prices','housing','4 features → price'],['Classification','classification','3 features, binary class']].map(([name,type,desc])=>(
              <div key={type} onClick={()=>loadData(null,name+'.csv',generateMockDataset(type))} style={{display:'flex',alignItems:'center',gap:'10px',padding:'10px',border:'1px solid var(--b0)',borderRadius:'3px',marginBottom:'6px',cursor:'pointer',background:'var(--raised)',transition:'all .12s'}}
                onMouseEnter={e=>{e.currentTarget.style.borderColor='var(--cyan)';e.currentTarget.style.background='var(--gcyan)'}}
                onMouseLeave={e=>{e.currentTarget.style.borderColor='var(--b0)';e.currentTarget.style.background='var(--raised)'}}>
                <span style={{fontSize:'18px',color:'var(--cyan)'}}>⬟</span>
                <div style={{flex:1}}><div style={{fontSize:'12px',fontWeight:600,color:'var(--t1)'}}>{name}</div><div style={{fontSize:'10px',color:'var(--t3)'}}>{desc}</div></div>
                <span style={{color:'var(--cyan)'}}>→</span>
              </div>
            ))}
            <LogPanel logs={logs} height={70}/>
          </Panel>
        </div>
      )}

      {/* ── OVERVIEW ── */}
      {tab==='overview' && data && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:'10px'}}>
            <Metric label="Rows" value={rows.length} color="var(--cyan)"/>
            <Metric label="Columns" value={headers.length} color="var(--green)"/>
            <Metric label="Numeric" value={nc.length} color="var(--violet)"/>
            <Metric label="Categorical" value={cc.length} color="var(--gold)"/>
            <Metric label="Total Nulls" value={types.reduce((s,t)=>s+t.nullCount,0)} color={types.reduce((s,t)=>s+t.nullCount,0)>0?'var(--red)':'var(--green)'}/>
          </div>

          <Panel title="COLUMN PROFILES" accent="var(--cyan)">
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(260px,1fr))',gap:'10px'}}>
              {types.map((ct,i)=>{
                const s = ct.type==='numeric' ? featureStats(rows,ct.col) : null;
                return (
                  <div key={ct.col} style={{padding:'12px',background:'var(--raised)',border:'1px solid var(--b0)',borderRadius:'3px',borderLeft:`3px solid ${P[i%P.length]}`}}>
                    <div style={{display:'flex',alignItems:'center',gap:'8px',marginBottom:'8px'}}>
                      <span style={{fontWeight:700,fontSize:'12px',color:'var(--t1)',flex:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{ct.col}</span>
                      <Tag color={ct.type==='numeric'?'var(--cyan)':'var(--gold)'}>{ct.type}</Tag>
                    </div>
                    {s && (
                      <>
                        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'4px',marginBottom:'6px'}}>
                          {[['mean',s.mean],['std',s.std],['min',s.min],['median',s.median],['max',s.max],['skew',s.skewness]].map(([k,v])=>(
                            <div key={k} style={{background:'var(--surface)',padding:'4px 6px',borderRadius:'2px'}}>
                              <div style={{fontSize:'8px',color:'var(--t3)'}}>{k}</div>
                              <div style={{fontSize:'10px',color:'var(--cyan)',fontWeight:600}}>{typeof v==='number'?v.toFixed(3):v}</div>
                            </div>
                          ))}
                        </div>
                        <div style={{display:'flex',gap:'6px'}}>
                          <Tag color={s.outlierCount>0?'var(--red)':'var(--green)'}>{s.outlierCount} outliers</Tag>
                          <Tag color={s.nullCount>0?'var(--gold)':'var(--t3)'}>{ct.nullCount} nulls</Tag>
                          {Math.abs(s.skewness)>1 && <Tag color="var(--orange)">skewed</Tag>}
                        </div>
                      </>
                    )}
                    {ct.type==='categorical' && (
                      <div style={{fontSize:'11px',color:'var(--t3)'}}>{ct.uniqueCount} unique · {ct.nullCount} nulls</div>
                    )}
                  </div>
                );
              })}
            </div>
          </Panel>

          {/* Preview */}
          <Panel title="DATA PREVIEW" badge="first 10 rows">
            <div style={{overflowX:'auto'}}>
              <table style={{borderCollapse:'collapse',fontSize:'10px',minWidth:'500px'}}>
                <thead><tr style={{background:'var(--deep)'}}>
                  {headers.map(h=><th key={h} style={{padding:'6px 10px',color:'var(--cyan)',fontSize:'9px',borderBottom:'1px solid var(--b0)',whiteSpace:'nowrap'}}>{h}</th>)}
                </tr></thead>
                <tbody>{rows.slice(0,10).map((row,i)=>(
                  <tr key={i} style={{borderBottom:'1px solid var(--b0)'}}>
                    {headers.map(h=><td key={h} style={{padding:'5px 10px',color:'var(--t2)',whiteSpace:'nowrap'}}>{row[h]||<span style={{color:'var(--red)',opacity:.5}}>∅</span>}</td>)}
                  </tr>
                ))}</tbody>
              </table>
            </div>
          </Panel>
        </div>
      )}

      {/* ── DISTRIBUTIONS ── */}
      {tab==='distributions' && data && (
        <div style={{display:'grid',gridTemplateColumns:'180px 1fr',gap:'12px'}}>
          <Panel title="COLUMN">
            <div style={{maxHeight:'400px',overflowY:'auto'}}>
              {nc.map(col=>(
                <div key={col} onClick={()=>setSelCol(col)} style={{padding:'8px 10px',cursor:'pointer',borderRadius:'2px',background:selCol===col?'var(--gcyan)':'transparent',border:`1px solid ${selCol===col?'var(--cyan)44':'transparent'}`,marginBottom:'2px',fontSize:'11px',color:selCol===col?'var(--cyan)':'var(--t1)',fontWeight:selCol===col?600:400}}>{col}</div>
              ))}
            </div>
          </Panel>
          {selCol && (() => {
            const s = featureStats(rows,selCol);
            if(!s) return <InfoBox color="var(--gold)" icon="⚠">No numeric values</InfoBox>;
            const vals = rows.map(r=>parseNum(r[selCol])).filter(v=>!isNaN(v)&&isFinite(v));
            const mn=Math.min(...vals),mx=Math.max(...vals),bins=25,bw=(mx-mn)/bins;
            const hist = Array.from({length:bins},(_,i)=>({bin:`${(mn+i*bw).toFixed(1)}`,count:vals.filter(v=>v>=mn+i*bw&&v<mn+(i+1)*bw).length}));
            return (
              <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
                <div style={{display:'grid',gridTemplateColumns:'repeat(6,1fr)',gap:'8px'}}>
                  {[['Mean',s.mean,'var(--cyan)'],['Std',s.std,'var(--green)'],['Skewness',s.skewness,Math.abs(s.skewness)>1?'var(--orange)':'var(--green)'],['Kurtosis',s.kurtosis,'var(--violet)'],['Outliers',`${s.outlierCount}(${s.outlierPct}%)`,s.outlierCount>0?'var(--red)':'var(--green)'],['CV%',s.cv+'%','var(--gold)']].map(([l,v,c])=>(
                    <Metric key={l} label={l} value={v} color={c}/>
                  ))}
                </div>
                <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:'6px'}}>
                  {[['Min',s.min],['Q1',s.q1],['Median',s.median],['Q3',s.q3],['Max',s.max]].map(([l,v])=>(
                    <div key={l} style={{background:'var(--raised)',border:'1px solid var(--b0)',padding:'8px',borderRadius:'3px',textAlign:'center'}}>
                      <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'3px'}}>{l}</div>
                      <div style={{fontSize:'13px',color:'var(--cyan)',fontWeight:700}}>{typeof v==='number'?v.toFixed(3):v}</div>
                    </div>
                  ))}
                </div>
                <Panel title="HISTOGRAM" badge={selCol} accent="var(--cyan)">
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={hist}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                      <XAxis dataKey="bin" stroke="var(--t3)" tick={{fontSize:8}} interval="preserveStartEnd"/>
                      <YAxis stroke="var(--t3)" tick={{fontSize:9}}/>
                      <Tooltip/>
                      <Bar dataKey="count" radius={[2,2,0,0]}>{hist.map((_,i)=><Cell key={i} fill={P[i%P.length]} opacity={.85}/>)}</Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Panel>
                {/* Box plot */}
                <Panel title="BOX PLOT" accent="var(--violet)">
                  <div style={{padding:'20px 20px 12px'}}>
                    {(()=>{
                      const range=s.max-s.min||1;
                      const p=v=>((v-s.min)/range)*100;
                      return (
                        <>
                          <div style={{height:'44px',position:'relative',marginBottom:'8px'}}>
                            <div style={{position:'absolute',left:`${p(s.min)}%`,width:`${p(s.q1)-p(s.min)}%`,height:'2px',background:'var(--t3)',top:'50%',transform:'translateY(-50%)'}}/>
                            <div style={{position:'absolute',left:`${p(s.q3)}%`,width:`${p(s.max)-p(s.q3)}%`,height:'2px',background:'var(--t3)',top:'50%',transform:'translateY(-50%)'}}/>
                            <div style={{position:'absolute',left:`${p(s.min)}%`,width:'2px',height:'16px',background:'var(--t3)',top:'50%',transform:'translateY(-50%)'}}/>
                            <div style={{position:'absolute',left:`${p(s.max)}%`,width:'2px',height:'16px',background:'var(--t3)',top:'50%',transform:'translateY(-50%)'}}/>
                            <div style={{position:'absolute',left:`${p(s.q1)}%`,width:`${p(s.q3)-p(s.q1)}%`,height:'32px',top:'50%',transform:'translateY(-50%)',background:'var(--gcyan)',border:'1px solid var(--cyan)',borderRadius:'2px'}}/>
                            <div style={{position:'absolute',left:`${p(s.median)}%`,width:'2px',height:'32px',top:'50%',transform:'translateY(-50%)',background:'var(--red)',zIndex:1}}/>
                            <div style={{position:'absolute',left:`${p(s.mean)}%`,width:'10px',height:'10px',borderRadius:'50%',background:'var(--gold)',top:'50%',transform:'translate(-50%,-50%)',zIndex:2}}/>
                          </div>
                          <div style={{display:'flex',justifyContent:'space-between',fontSize:'9px',color:'var(--t3)'}}>
                            <span>{s.min}</span><span style={{color:'var(--green)'}}>Q1:{s.q1}</span><span style={{color:'var(--red)'}}>Median:{s.median}</span><span style={{color:'var(--green)'}}>Q3:{s.q3}</span><span>{s.max}</span>
                          </div>
                          <div style={{display:'flex',gap:'12px',fontSize:'9px',marginTop:'6px'}}>
                            <span style={{color:'var(--red)'}}>━ median</span><span style={{color:'var(--gold)'}}>● mean</span><span style={{color:'var(--cyan)'}}>□ IQR</span>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </Panel>
              </div>
            );
          })()}
        </div>
      )}

      {/* ── CORRELATIONS ── */}
      {tab==='correlations' && data && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <div style={{display:'flex',gap:'10px',flexWrap:'wrap'}}>
            <Btn onClick={()=>{
              const pairs=[];
              nc.forEach(c1=>nc.forEach(c2=>{if(c1<c2){const mi=mutualInfo(rows,c1,c2);pairs.push({c1,c2,mi,label:`${c1}↔${c2}`});}}));
              setMiMatrix(pairs.sort((a,b)=>b.mi-a.mi));
              log(`MI computed for ${pairs.length} pairs`,'ok');
            }} color="var(--cyan)">COMPUTE MUTUAL INFORMATION →</Btn>
            <Btn onClick={()=>{
              const corrs=nc.map((c1,i)=>{const obj={col:c1};nc.forEach(c2=>{const v1=rows.map(r=>parseNum(r[c1])).filter(v=>!isNaN(v));const v2=rows.map(r=>parseNum(r[c2])).filter(v=>!isNaN(v));const n=Math.min(v1.length,v2.length);const m1=mean(v1.slice(0,n)),m2=mean(v2.slice(0,n));let num=0,d1=0,d2=0;for(let i=0;i<n;i++){num+=(v1[i]-m1)*(v2[i]-m2);d1+=(v1[i]-m1)**2;d2+=(v2[i]-m2)**2;}obj[c2]=d1&&d2?+(num/Math.sqrt(d1*d2)).toFixed(3):0;});return obj;});
              setCorrMatrix(corrs);
              log('Pearson correlation matrix built','ok');
            }} color="var(--green)">PEARSON CORRELATION MATRIX →</Btn>
          </div>

          {miMatrix.length>0 && (
            <Panel title="MUTUAL INFORMATION — FEATURE REDUNDANCY DETECTION" badge="higher = more shared info" accent="var(--cyan)">
              <ResponsiveContainer width="100%" height={Math.min(400,miMatrix.length*32+40)}>
                <BarChart data={miMatrix.slice(0,15)} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)" horizontal={false}/>
                  <XAxis type="number" stroke="var(--t3)" tick={{fontSize:9}}/>
                  <YAxis type="category" dataKey="label" stroke="var(--t3)" tick={{fontSize:9}} width={120}/>
                  <Tooltip/>
                  <Bar dataKey="mi" radius={[0,2,2,0]} name="MI Score">{miMatrix.slice(0,15).map((_,i)=><Cell key={i} fill={P[i%P.length]} opacity={.85}/>)}</Bar>
                </BarChart>
              </ResponsiveContainer>
              <InfoBox color="var(--t3)" icon="◈">High MI between two features = redundancy. Consider dropping one. High MI with target = informative feature.</InfoBox>
            </Panel>
          )}

          {corrMatrix.length>0 && (
            <Panel title="PEARSON CORRELATION HEATMAP" accent="var(--green)">
              <div style={{overflowX:'auto'}}>
                <div style={{display:'flex',gap:'2px',marginLeft:'110px',marginBottom:'3px'}}>
                  {nc.map(c=><div key={c} style={{width:'72px',fontSize:'8px',color:'var(--t3)',textAlign:'center',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{c}</div>)}
                </div>
                {corrMatrix.map((row,i)=>(
                  <div key={i} style={{display:'flex',gap:'2px',alignItems:'center',marginBottom:'2px'}}>
                    <div style={{width:'108px',fontSize:'8px',color:'var(--t3)',textAlign:'right',paddingRight:'6px'}}>{row.col}</div>
                    {nc.map(c=>{const v=row[c]||0,abs=Math.abs(v);return <div key={c} style={{width:'72px',height:'44px',borderRadius:'2px',background:v>=0?`rgba(0,217,255,${abs*.85})`:`rgba(255,53,86,${abs*.85})`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:'10px',fontWeight:abs>.6?700:400,color:abs>.5?'var(--void)':'var(--t2)'}} title={`r(${row.col},${c})=${v}`}>{v.toFixed(2)}</div>;})}
                  </div>
                ))}
                <div style={{display:'flex',gap:'16px',marginTop:'8px',fontSize:'10px'}}>
                  <span style={{color:'var(--cyan)'}}>■ positive</span><span style={{color:'var(--red)'}}>■ negative</span><span style={{color:'var(--t3)'}}>intensity = |r|</span>
                </div>
              </div>
            </Panel>
          )}
        </div>
      )}

      {/* ── ENCODE ── */}
      {tab==='encode' && data && (
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
          <Panel title="CATEGORICAL ENCODING" accent="var(--cyan)">
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'8px',marginBottom:'10px'}}>
              <Sel value={encodeCol} onChange={setEncodeCol} options={cc.length?cc:headers} label="COLUMN"/>
              <Sel value={encodeMethod} onChange={setEncodeMethod} options={['onehot','label']} label="METHOD"/>
            </div>
            <Btn onClick={()=>{
              if(encodeMethod==='onehot'){const r=oneHotEncode(rows,encodeCol);applyTransform({rows:r.rows,newCols:r.newCols},`One-hot: "${encodeCol}" → ${r.newCols.length} binary cols`);}
              else{const r=labelEncode(rows,encodeCol);applyTransform({rows:r.rows,newCol:r.newCol},`Label encode "${encodeCol}" → "${r.newCol}" (${Object.keys(r.mapping).slice(0,3).map(k=>`${k}→${r.mapping[k]}`).join(',')}...)`);}
            }} color="var(--cyan)">ENCODE →</Btn>
            <div style={{marginTop:'10px',padding:'10px',background:'var(--raised)',borderRadius:'3px',fontSize:'11px',color:'var(--t3)'}}>
              <strong style={{color:'var(--cyan)'}}>One-Hot:</strong> Creates binary column per unique value. No ordinal assumption. Works for tree & distance models.<br/><br/>
              <strong style={{color:'var(--gold)'}}>Label:</strong> Maps to integers 0,1,2… Compact but implies ordinal relationship. Use for tree models only.
            </div>
          </Panel>

          <Panel title="BINNING / DISCRETIZATION" accent="var(--violet)">
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'8px',marginBottom:'8px'}}>
              <Sel value={binCol} onChange={setBinCol} options={nc} label="NUMERIC COLUMN"/>
              <Sel value={binStrat} onChange={setBinStrat} options={['equal_width','equal_freq']} label="STRATEGY"/>
            </div>
            <div style={{marginBottom:'10px'}}>
              <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'4px'}}>BINS: {binN}</div>
              <input type="range" min={2} max={10} value={binN} onChange={e=>setBinN(parseInt(e.target.value))} style={{width:'100%',accentColor:'var(--violet)'}}/>
            </div>
            <Btn onClick={()=>{
              const r=binColumn(rows,binCol,binN,binStrat);
              applyTransform({rows:r.rows,newCol:r.newCol},`Binned "${binCol}" → ${r.labels.length} bins (${binStrat}): ${r.labels.slice(0,2).join(', ')}...`);
            }} color="var(--violet)">BIN COLUMN →</Btn>
            <div style={{marginTop:'10px',padding:'10px',background:'var(--raised)',borderRadius:'3px',fontSize:'11px',color:'var(--t3)'}}>
              <strong style={{color:'var(--violet)'}}>Equal Width:</strong> Same interval size. Fast, but sensitive to outliers.<br/><br/>
              <strong style={{color:'var(--cyan)'}}>Equal Freq:</strong> Same number of points per bin. More robust, like quantile binning.
            </div>
          </Panel>

          <Panel title="NORMALIZATION / STANDARDIZATION" accent="var(--green)" style={{gridColumn:'1/-1'}}>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'12px',alignItems:'start'}}>
              <div>
                <Sel value={normMethod} onChange={setNormMethod} options={['minmax','zscore']} label="METHOD"/>
                <div style={{marginTop:'10px'}}>
                  <Btn onClick={()=>{
                    const r=normalizeDataset(rows,nc,normMethod);
                    const newTypes=inferTypes(headers,r.rows);
                    setData({rows:r.rows,headers,types:newTypes});
                    log(`${normMethod==='minmax'?'Min-Max [0,1]':'Z-score (μ=0,σ=1)'} applied to ${nc.length} numeric cols`,'ok');
                  }} color="var(--green)">NORMALIZE ALL NUMERIC →</Btn>
                </div>
              </div>
              <div style={{padding:'12px',background:'var(--raised)',borderRadius:'3px',fontSize:'11px',color:'var(--t3)'}}>
                <strong style={{color:'var(--green)'}}>Min-Max:</strong> x' = (x−min)/(max−min) → [0,1]<br/>
                Good for: neural networks, distance-based models (KNN, SVM).<br/>
                Sensitive to outliers.
              </div>
              <div style={{padding:'12px',background:'var(--raised)',borderRadius:'3px',fontSize:'11px',color:'var(--t3)'}}>
                <strong style={{color:'var(--cyan)'}}>Z-Score:</strong> x' = (x−μ)/σ → N(0,1)<br/>
                Good for: linear regression, logistic regression, PCA.<br/>
                Robust to outliers.
              </div>
            </div>
          </Panel>
        </div>
      )}

      {/* ── TRANSFORM ── */}
      {tab==='transform' && data && (
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
          <Panel title="LOG TRANSFORM — Fix Skewed Distributions" accent="var(--orange)">
            <Sel value={logCol} onChange={setLogCol} options={nc} label="COLUMN (must be > 0)"/>
            <div style={{height:'10px'}}/>
            <Btn onClick={()=>{
              const r=logTransform(rows,logCol);
              applyTransform({rows:r.rows,newCol:r.newCol},`log("${logCol}") → "${r.newCol}"`);
            }} color="var(--orange)">APPLY LOG(x) →</Btn>
            <div style={{marginTop:'10px',padding:'10px',background:'var(--raised)',borderRadius:'3px',fontSize:'11px',color:'var(--t3)'}}>
              Reduces right-skewed distributions. Use on: salary, price, count data.<br/>
              Turns multiplicative relationships into additive ones (linear regression friendly).
            </div>
          </Panel>

          <Panel title="INTERACTION FEATURES — C1 × C2" accent="var(--violet)">
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'8px',marginBottom:'10px'}}>
              <Sel value={intC1} onChange={setIntC1} options={nc} label="FEATURE 1"/>
              <Sel value={intC2} onChange={setIntC2} options={nc} label="FEATURE 2"/>
            </div>
            <Btn onClick={()=>{
              const r=interactionFeature(rows,intC1,intC2);
              applyTransform({rows:r.rows,newCol:r.newCol},`Interaction: "${r.newCol}" = ${intC1} × ${intC2}`);
            }} color="var(--violet)">CREATE C1 × C2 →</Btn>
            <div style={{marginTop:'10px',padding:'10px',background:'var(--raised)',borderRadius:'3px',fontSize:'11px',color:'var(--t3)'}}>
              Captures synergy between two features. E.g. rooms × sqft might explain price better than either alone.
            </div>
          </Panel>

          <Panel title="POLYNOMIAL FEATURES — Degree 2" accent="var(--gold)" style={{gridColumn:'1/-1'}}>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'12px',alignItems:'end'}}>
              <Sel value={polyC1} onChange={setPolyC1} options={nc} label="FEATURE 1"/>
              <Sel value={polyC2} onChange={setPolyC2} options={nc} label="FEATURE 2"/>
              <Btn onClick={()=>{
                const r=polyFeatures(rows,polyC1,polyC2,2);
                applyTransform({rows:r.rows,newCols:r.newCols},`Poly features: ${r.newCols.join(', ')}`);
              }} color="var(--gold)">GENERATE DEGREE-2 FEATURES →</Btn>
            </div>
            <div style={{marginTop:'10px',padding:'10px',background:'var(--raised)',borderRadius:'3px',fontSize:'11px',color:'var(--t3)'}}>
              Degree-2 creates: x₁², x₁·x₂, x₂² — expands model's decision boundary for nonlinear patterns.<br/>
              Warning: degree-3+ on many features causes explosion (d+k choose k) in feature count.
            </div>
          </Panel>

          <Panel title="CURRENT DATASET" badge={`${rows.length}×${headers.length}`} style={{gridColumn:'1/-1'}}>
            <div style={{overflowX:'auto'}}>
              <table style={{borderCollapse:'collapse',fontSize:'10px',minWidth:'400px'}}>
                <thead><tr style={{background:'var(--deep)'}}>
                  {headers.map(h=><th key={h} style={{padding:'6px 10px',color:'var(--cyan)',fontSize:'9px',borderBottom:'1px solid var(--b0)',whiteSpace:'nowrap'}}>{h}</th>)}
                </tr></thead>
                <tbody>{rows.slice(0,6).map((row,i)=>(
                  <tr key={i} style={{borderBottom:'1px solid var(--b0)'}}>
                    {headers.map(h=><td key={h} style={{padding:'5px 10px',color:'var(--t2)',whiteSpace:'nowrap'}}>{row[h]||'∅'}</td>)}
                  </tr>
                ))}</tbody>
              </table>
            </div>
            <LogPanel logs={logs} height={60}/>
          </Panel>
        </div>
      )}

      {/* ── SCALING ── */}
      {tab==='scaling' && data && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="MULTI-COLUMN SCALING & NORMALIZATION" accent="var(--cyan)">
            <div style={{marginBottom:'10px',fontSize:'11px',color:'var(--t3)'}}>
              Apply Min-Max scaling [0,1] or Z-Score standardization (μ=0, σ=1) across multiple columns simultaneously.
              Normalizing features is essential before distance-based algorithms (k-NN, k-Means, SVM) and gradient descent.
            </div>
            <div style={{marginBottom:'12px'}}>
              <div style={{fontSize:'10px',color:'var(--t3)',marginBottom:'8px',letterSpacing:'.1em'}}>SELECT COLUMNS TO SCALE</div>
              <div style={{display:'flex',flexWrap:'wrap',gap:'6px',marginBottom:'12px'}}>
                {nc.map(col=>(
                  <button key={col} onClick={()=>setScaleCols(s=>s.includes(col)?s.filter(c=>c!==col):[...s,col])}
                    style={{padding:'5px 12px',border:`1px solid ${scaleCols.includes(col)?'var(--cyan)':'var(--b0)'}`,background:scaleCols.includes(col)?'var(--gcyan)':'var(--raised)',color:scaleCols.includes(col)?'var(--cyan)':'var(--t2)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono'}}>
                    {col}
                  </button>
                ))}
                <button onClick={()=>setScaleCols(nc)} style={{padding:'5px 10px',border:'1px solid var(--b1)',background:'var(--raised)',color:'var(--t3)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono'}}>ALL</button>
                <button onClick={()=>setScaleCols([])} style={{padding:'5px 10px',border:'1px solid var(--b1)',background:'var(--raised)',color:'var(--t3)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono'}}>NONE</button>
              </div>
              <div style={{display:'flex',gap:'10px',alignItems:'flex-end',flexWrap:'wrap'}}>
                <div>
                  <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'6px',letterSpacing:'.1em'}}>METHOD</div>
                  <div style={{display:'flex',gap:'6px'}}>
                    {[['minmax','Min-Max [0,1]'],['zscore','Z-Score (μ=0,σ=1)']].map(([v,l])=>(
                      <button key={v} onClick={()=>setScaleMethod(v)} style={{padding:'7px 14px',border:`1px solid ${scaleMethod===v?'var(--cyan)':'var(--b0)'}`,background:scaleMethod===v?'var(--gcyan)':'var(--raised)',color:scaleMethod===v?'var(--cyan)':'var(--t2)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono',fontWeight:scaleMethod===v?700:400}}>{l}</button>
                    ))}
                  </div>
                </div>
                <button onClick={()=>{
                  if(!scaleCols.length){return;}
                  const stats=scaleCols.map(col=>{
                    const vals=rows.map(r=>parseNum(r[col])).filter(v=>!isNaN(v));
                    const mn=Math.min(...vals),mx=Math.max(...vals);
                    const m=vals.reduce((s,v)=>s+v,0)/vals.length;
                    const s=Math.sqrt(vals.reduce((s,v)=>s+(v-m)**2,0)/vals.length);
                    let scaled;
                    if(scaleMethod==='minmax'){scaled=vals.map(v=>mx>mn?+((v-mn)/(mx-mn)).toFixed(4):0);}
                    else{scaled=vals.map(v=>s>0?+((v-m)/s).toFixed(4):0);}
                    const sMn=Math.min(...scaled),sMx=Math.max(...scaled),sM=scaled.reduce((s,v)=>s+v,0)/scaled.length,sSt=Math.sqrt(scaled.reduce((s,v)=>s+(v-sM)**2,0)/scaled.length);
                    return{col,origMin:mn.toFixed(3),origMax:mx.toFixed(3),origMean:m.toFixed(3),origStd:s.toFixed(3),scaledMin:sMn.toFixed(3),scaledMax:sMx.toFixed(3),scaledMean:sM.toFixed(4),scaledStd:sSt.toFixed(4),scaled};
                  });
                  const sample=rows.slice(0,5).map(r=>{const out={...r};scaleCols.forEach((col,ci)=>{const vals=rows.map(row=>parseNum(row[col])).filter(v=>!isNaN(v));const mn=Math.min(...vals),mx=Math.max(...vals),m=vals.reduce((s,v)=>s+v,0)/vals.length,s=Math.sqrt(vals.reduce((s,v)=>s+(v-m)**2,0)/vals.length);const v=parseNum(r[col]);out[`${col}_scaled`]=scaleMethod==='minmax'?(mx>mn?+((v-mn)/(mx-mn)).toFixed(4):0):(s>0?+((v-m)/s).toFixed(4):0);});return out;});
                  setScaleResult({stats,sample,method:scaleMethod,cols:scaleCols});
                }} style={{padding:'9px 18px',background:'var(--gcyan)',border:'1px solid var(--cyan)',color:'var(--cyan)',fontSize:'10px',cursor:'pointer',borderRadius:'3px',fontFamily:'JetBrains Mono',fontWeight:700,letterSpacing:'.08em',opacity:scaleCols.length?1:.4}}>SCALE COLUMNS →</button>
              </div>
            </div>
          </Panel>

          {scaleResult&&(
            <>
              <Panel title={`BEFORE / AFTER STATS — ${scaleResult.method==='minmax'?'Min-Max [0,1]':'Z-Score (μ=0,σ=1)'}`} accent="var(--cyan)">
                <div style={{overflowX:'auto'}}>
                  <table style={{width:'100%',borderCollapse:'collapse',fontSize:'11px',minWidth:'600px'}}>
                    <thead><tr style={{background:'var(--deep)'}}>
                      <th style={{padding:'7px 12px',textAlign:'left',color:'var(--t3)',fontSize:'9px',borderBottom:'1px solid var(--b0)'}}>COLUMN</th>
                      <th colSpan={4} style={{padding:'7px 12px',textAlign:'center',color:'var(--orange)',fontSize:'9px',borderBottom:'1px solid var(--b0)',borderLeft:'1px solid var(--b0)'}}>ORIGINAL</th>
                      <th colSpan={4} style={{padding:'7px 12px',textAlign:'center',color:'var(--cyan)',fontSize:'9px',borderBottom:'1px solid var(--b0)',borderLeft:'1px solid var(--b0)'}}>SCALED</th>
                    </tr>
                    <tr style={{background:'var(--raised)'}}>
                      <th style={{padding:'5px 12px',color:'var(--t3)',fontSize:'9px',borderBottom:'1px solid var(--b0)'}}></th>
                      {['Min','Max','Mean','Std','Min','Max','Mean','Std'].map((h,i)=><th key={i} style={{padding:'5px 10px',textAlign:'right',color:i<4?'var(--orange)':'var(--cyan)',fontSize:'9px',borderBottom:'1px solid var(--b0)',borderLeft:i===4?'1px solid var(--b0)':'none'}}>{h}</th>)}
                    </tr></thead>
                    <tbody>{scaleResult.stats.map((s,i)=>(
                      <tr key={s.col} style={{borderBottom:'1px solid var(--b0)',background:i%2===0?'transparent':'rgba(0,0,0,.05)'}}>
                        <td style={{padding:'7px 12px',color:'var(--t1)',fontWeight:600,fontFamily:'JetBrains Mono',fontSize:'10px'}}>{s.col}</td>
                        <td style={{padding:'7px 10px',textAlign:'right',color:'var(--t2)',fontFamily:'JetBrains Mono'}}>{s.origMin}</td>
                        <td style={{padding:'7px 10px',textAlign:'right',color:'var(--t2)',fontFamily:'JetBrains Mono'}}>{s.origMax}</td>
                        <td style={{padding:'7px 10px',textAlign:'right',color:'var(--t2)',fontFamily:'JetBrains Mono'}}>{s.origMean}</td>
                        <td style={{padding:'7px 10px',textAlign:'right',color:'var(--t2)',fontFamily:'JetBrains Mono'}}>{s.origStd}</td>
                        <td style={{padding:'7px 10px',textAlign:'right',color:'var(--cyan)',fontFamily:'JetBrains Mono',fontWeight:600,borderLeft:'1px solid var(--b0)'}}>{s.scaledMin}</td>
                        <td style={{padding:'7px 10px',textAlign:'right',color:'var(--cyan)',fontFamily:'JetBrains Mono',fontWeight:600}}>{s.scaledMax}</td>
                        <td style={{padding:'7px 10px',textAlign:'right',color:'var(--cyan)',fontFamily:'JetBrains Mono',fontWeight:600}}>{s.scaledMean}</td>
                        <td style={{padding:'7px 10px',textAlign:'right',color:'var(--cyan)',fontFamily:'JetBrains Mono',fontWeight:600}}>{s.scaledStd}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              </Panel>

              <Panel title="SAMPLE ROWS (first 5) — original + scaled values" accent="var(--green)">
                <div style={{overflowX:'auto'}}>
                  <table style={{width:'100%',borderCollapse:'collapse',fontSize:'10px',minWidth:'500px'}}>
                    <thead><tr style={{background:'var(--deep)'}}>
                      {scaleResult.cols.map(col=><>
                        <th key={col+'_orig'} style={{padding:'6px 10px',textAlign:'right',color:'var(--orange)',fontSize:'9px',borderBottom:'1px solid var(--b0)'}}>{col}</th>
                        <th key={col+'_sc'} style={{padding:'6px 10px',textAlign:'right',color:'var(--cyan)',fontSize:'9px',borderBottom:'1px solid var(--b0)',background:'rgba(0,200,255,.05)'}}>{col}_scaled</th>
                      </>)}
                    </tr></thead>
                    <tbody>{scaleResult.sample.map((row,i)=>(
                      <tr key={i} style={{borderBottom:'1px solid var(--b0)'}}>
                        {scaleResult.cols.map(col=><>
                          <td key={col+'_o'} style={{padding:'5px 10px',textAlign:'right',color:'var(--t2)',fontFamily:'JetBrains Mono'}}>{row[col]}</td>
                          <td key={col+'_s'} style={{padding:'5px 10px',textAlign:'right',color:'var(--cyan)',fontFamily:'JetBrains Mono',background:'rgba(0,200,255,.03)',fontWeight:600}}>{row[`${col}_scaled`]}</td>
                        </>)}
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              </Panel>

              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'10px'}}>
                {[
                  {name:'Min-Max [0,1]',formula:'x_new = (x − min) / (max − min)',range:'[0, 1]',pros:'Bounded, good for neural nets',cons:'Sensitive to outliers',use:'Image pixels, bounded inputs'},
                  {name:'Z-Score (Standard)',formula:'x_new = (x − μ) / σ',range:'[-∞, +∞]',pros:'Handles outliers better',cons:'Unbounded range',use:'k-NN, SVM, PCA, regression'},
                  {name:'Robust Scaling',formula:'x_new = (x − Q2) / IQR',range:'Varies',pros:'Robust to extreme outliers',cons:'Must compute quartiles',use:'Data with heavy outliers'},
                ].map(m=>(
                  <div key={m.name} style={{padding:'12px',background:'var(--raised)',border:'1px solid var(--b0)',borderRadius:'3px',borderTop:`3px solid var(--cyan)`}}>
                    <div style={{fontWeight:700,color:'var(--cyan)',marginBottom:'6px',fontSize:'11px'}}>{m.name}</div>
                    <div style={{fontFamily:'JetBrains Mono',fontSize:'10px',color:'var(--green)',marginBottom:'6px',background:'var(--deep)',padding:'6px',borderRadius:'2px'}}>{m.formula}</div>
                    <div style={{fontSize:'9px',color:'var(--t3)',display:'grid',gridTemplateColumns:'auto 1fr',gap:'2px 8px'}}>
                      <span style={{color:'var(--t3)'}}>Range:</span><span style={{color:'var(--t2)'}}>{m.range}</span>
                      <span style={{color:'var(--green)'}}>Pros:</span><span style={{color:'var(--t2)'}}>{m.pros}</span>
                      <span style={{color:'var(--red)'}}>Cons:</span><span style={{color:'var(--t2)'}}>{m.cons}</span>
                      <span style={{color:'var(--cyan)'}}>Use for:</span><span style={{color:'var(--t2)'}}>{m.use}</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* ── PCA ── */}
      {tab==='pca' && data && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="PRINCIPAL COMPONENT ANALYSIS" accent="var(--cyan)">
            <div style={{marginBottom:'10px',fontSize:'11px',color:'var(--t3)'}}>
              Select 2+ numeric features. PCA uses the covariance matrix and extracts principal components via power iteration. 
              Result: orthogonal axes that maximize variance. Used for visualization, noise reduction, and feature compression.
            </div>
            <div style={{display:'flex',flexWrap:'wrap',gap:'6px',marginBottom:'12px'}}>
              {nc.map(col=>(
                <button key={col} onClick={()=>setPcaCols(s=>s.includes(col)?s.filter(c=>c!==col):[...s,col])}
                  style={{padding:'5px 12px',border:`1px solid ${pcaCols.includes(col)?'var(--cyan)':'var(--b0)'}`,background:pcaCols.includes(col)?'var(--gcyan)':'var(--raised)',color:pcaCols.includes(col)?'var(--cyan)':'var(--t2)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono'}}>
                  {col}
                </button>
              ))}
            </div>
            <Btn onClick={()=>{
              if(pcaCols.length<2){log('Select at least 2 columns','err');return;}
              const r=runPCA(rows,pcaCols,2);
              if(!r){log('PCA failed — need more clean data','err');return;}
              setPcaResult(r);
              log(`PCA: PC1=${r.explainedVar[0]}%, PC2=${r.explainedVar[1]}% | Total=${(r.explainedVar[0]+r.explainedVar[1]).toFixed(1)}%`,'ok');
            }} color="var(--cyan)">RUN PCA →</Btn>
          </Panel>

          {pcaResult && (
            <>
              <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'10px'}}>
                <Metric label="PC1 Variance" value={`${pcaResult.explainedVar[0]}%`} color="var(--cyan)"/>
                <Metric label="PC2 Variance" value={`${pcaResult.explainedVar[1]}%`} color="var(--green)"/>
                <Metric label="2D Total" value={`${(pcaResult.explainedVar[0]+pcaResult.explainedVar[1]).toFixed(1)}%`} color="var(--violet)"/>
                <Metric label="Features Used" value={pcaResult.cols.length} color="var(--t2)"/>
              </div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
                <Panel title="PCA SCATTER — PC1 vs PC2" accent="var(--cyan)">
                  <ResponsiveContainer width="100%" height={300}>
                    <ScatterChart>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                      <XAxis dataKey="pc1" name="PC1" stroke="var(--t3)" tick={{fontSize:9}} label={{value:`PC1 (${pcaResult.explainedVar[0]}%)`,position:'insideBottom',offset:-5,fill:'var(--t3)',fontSize:9}}/>
                      <YAxis dataKey="pc2" name="PC2" stroke="var(--t3)" tick={{fontSize:9}} label={{value:`PC2 (${pcaResult.explainedVar[1]}%)`,angle:-90,position:'insideLeft',fill:'var(--t3)',fontSize:9}}/>
                      <Tooltip/>
                      <Scatter data={pcaResult.projected} fill="var(--cyan)" opacity={0.6}/>
                    </ScatterChart>
                  </ResponsiveContainer>
                </Panel>
                <Panel title="FEATURE LOADINGS (how each feature contributes)" accent="var(--green)">
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={pcaResult.loadings} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)" horizontal={false}/>
                      <XAxis type="number" stroke="var(--t3)" tick={{fontSize:9}} domain={[-1,1]}/>
                      <YAxis type="category" dataKey="col" stroke="var(--t3)" tick={{fontSize:9}} width={80}/>
                      <Tooltip/><Legend/>
                      <ReferenceLine x={0} stroke="var(--b1)"/>
                      <Bar dataKey="pc1" fill="var(--cyan)" opacity={.85} name="PC1" radius={[0,2,2,0]}/>
                      <Bar dataKey="pc2" fill="var(--green)" opacity={.85} name="PC2" radius={[0,2,2,0]}/>
                    </BarChart>
                  </ResponsiveContainer>
                </Panel>
              </div>
              <Panel title="SCREE PLOT — Variance per Component" accent="var(--violet)">
                <ResponsiveContainer width="100%" height={150}>
                  <BarChart data={pcaResult.explainedVar.map((v,i)=>({component:`PC${i+1}`,variance:v}))}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                    <XAxis dataKey="component" stroke="var(--t3)" tick={{fontSize:9}}/>
                    <YAxis stroke="var(--t3)" tick={{fontSize:9}} unit="%"/>
                    <Tooltip formatter={v=>[`${v}%`,'Variance']}/>
                    <Bar dataKey="variance" fill="var(--violet)" radius={[2,2,0,0]} opacity={.85}/>
                  </BarChart>
                </ResponsiveContainer>
                <InfoBox color="var(--t3)" icon="◈">Look for the "elbow" in the scree plot — components before it capture meaningful structure. After the elbow is mostly noise.</InfoBox>
              </Panel>
            </>
          )}
        </div>
      )}

      {/* ── EXPORT ── */}
      {tab==='export' && data && (
        <Panel title="EXPORT ENGINEERED DATASET" accent="var(--green)">
          <div style={{marginBottom:'14px'}}>
            <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'10px',marginBottom:'12px'}}>
              <Metric label="Original Columns" value={orig?.headers.length||0} color="var(--t3)"/>
              <Metric label="Current Columns" value={headers.length} color="var(--green)"/>
              <Metric label="New Features Added" value={headers.length-(orig?.headers.length||0)} color="var(--cyan)"/>
            </div>
            <div style={{display:'flex',flexWrap:'wrap',gap:'6px',marginBottom:'12px'}}>
              {headers.map((h,i)=>{
                const isNew=!(orig?.headers||[]).includes(h);
                return <Tag key={h} color={isNew?'var(--green)':'var(--t3)'}>{h}{isNew?' ★':''}</Tag>;
              })}
            </div>
            <Btn onClick={()=>{
              const csv=[headers.join(','),...rows.map(r=>headers.map(h=>r[h]||'').join(','))].join('\n');
              const blob=new Blob([csv],{type:'text/csv'});
              const a=document.createElement('a');a.href=URL.createObjectURL(blob);a.download='engineered_features.csv';a.click();
              log('Downloaded engineered_features.csv','ok');
            }} color="var(--green)">⬇ DOWNLOAD ENGINEERED CSV</Btn>
          </div>
          <LogPanel logs={logs} height={100}/>
        </Panel>
      )}

      {!['upload'].includes(tab) && <LogPanel logs={logs} height={55}/>}
    </div>
  );
}
